/**
 * Interactive Plan Generator
 *
 * Produces a self-contained HTML string (inlined CSS + JS, no external deps)
 * for a click-through client-facing presentation of the estate plan.
 *
 * Input: the same flat render-variables dict consumed by the DOCX templates
 * (see fiduciaryRender.js → computeRenderVariables). Dispatches on
 * `v.package_type` to produce the right disposition narrative.
 *
 * Supported packages: Disclaimer, Fractional QTIP, Probate Avoidance.
 *
 * Navigation:
 *   → / Space / click     advance
 *   ←                     back
 *   Home / End            first / last
 */

// ---------------------------------------------------------------------------
// HTML escaping
// ---------------------------------------------------------------------------

function esc(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// ---------------------------------------------------------------------------
// Package detection
// ---------------------------------------------------------------------------

const PKG_DISCLAIMER = 'disclaimer';
const PKG_QTIP = 'qtip';
const PKG_PROBATE = 'probate';

function detectPackage(packageType) {
  const s = String(packageType || '').toLowerCase();
  if (s.includes('qtip')) return PKG_QTIP;
  if (s.includes('probate')) return PKG_PROBATE;
  return PKG_DISCLAIMER;
}

function packageDisplayName(v) {
  return v.package_type || 'Estate Plan';
}

// ---------------------------------------------------------------------------
// Slide primitives
// ---------------------------------------------------------------------------

function slide(headline, body, { kicker = '', footnote = '' } = {}) {
  return `
    <section class="slide">
      <div class="slide-inner">
        ${kicker ? `<div class="kicker">${esc(kicker)}</div>` : ''}
        <h1 class="headline">${headline}</h1>
        <div class="body">${body}</div>
        ${footnote ? `<div class="footnote">${esc(footnote)}</div>` : ''}
      </div>
    </section>
  `;
}

function fiduciarySlide(title, rows, opts) {
  const items = rows
    .filter(r => r.name)
    .map(r => `
      <li>
        <span class="label">${esc(r.label)}</span>
        <span class="name">${esc(r.name)}</span>
      </li>
    `)
    .join('');
  return slide(
    esc(title),
    `<ul class="fid-list">${items}</ul>`,
    opts,
  );
}

// ---------------------------------------------------------------------------
// Shared slides (cover, overview, ages, fiduciaries, closing)
// ---------------------------------------------------------------------------

function buildCover(v) {
  const couple = v.spouse
    ? `${esc(v.client)} <span class="and">and</span> ${esc(v.spouse)}`
    : esc(v.client);
  return `
    <section class="slide cover">
      <div class="slide-inner">
        <div class="kicker">Estate Plan</div>
        <h1 class="cover-title">${couple}</h1>
        <div class="cover-meta">
          ${esc(packageDisplayName(v))}
          ${v.signing_date ? ` &middot; ${esc(v.signing_date)}` : ''}
        </div>
        <div class="cover-cta">Press → or click to begin</div>
      </div>
    </section>
  `;
}

function buildOverview() {
  return slide(
    'Your plan in two parts',
    `<ol class="big-list">
       <li>How your assets pass</li>
       <li>Who carries it out</li>
     </ol>`,
  );
}

function buildAgeSlide(v) {
  const rows = [];
  if (v.beneficiary_cotrust_age)
    rows.push({ age: v.beneficiary_cotrust_age, label: 'becomes <strong>co-trustee</strong> of their trust' });
  if (v.beneficiary_sole_trust_age)
    rows.push({ age: v.beneficiary_sole_trust_age, label: 'becomes <strong>sole trustee</strong>' });
  if (v.trust_protector_age)
    rows.push({ age: v.trust_protector_age, label: 'may serve as <strong>Trust Protector</strong>' });

  return slide(
    `Each beneficiary's trust grows with them`,
    `<div class="age-ladder">
       ${rows.map(r => `
         <div class="age-rung">
           <span class="age-num">${esc(r.age)}</span>
           <span class="age-text">${r.label}</span>
         </div>
       `).join('')}
     </div>`,
  );
}

function buildMinorsSlides(v) {
  if (v.has_minor_children) {
    return [
      slide(
        `One combined trust while any child is young`,
        `<div class="age-callout">
           <div class="age-big">${esc(v.pot_trust_termination_age)}</div>
           <div class="age-caption">
             While any child is under age ${esc(v.pot_trust_termination_age)},
             all descendants share a single <strong>Descendants' Trust</strong>.<br/>
             Distributions are flexible, based on each child's needs.
           </div>
         </div>`,
        { kicker: 'If minors' },
      ),
      slide(
        `Then — separate trusts`,
        `<p class="lead">
           When the youngest living descendant reaches age ${esc(v.pot_trust_termination_age)},
           the trust divides into <strong>separate trusts, one per descendant</strong>.
         </p>`,
      ),
    ];
  }
  return [
    slide(
      `Separate trusts, one per descendant`,
      `<p class="lead">Each descendant receives their own trust immediately.</p>`,
    ),
  ];
}

function buildSpecificGiftsSlides(v) {
  const gifts = Array.isArray(v.specific_gifts) ? v.specific_gifts : [];
  if (!v.has_specific_gifts || gifts.length === 0) return [];

  const items = gifts.map(g => `
    <li class="gift-item">
      <div class="gift-description">${esc(g.description)}</div>
      <div class="gift-recipient">
        <span class="gift-arrow">&rarr;</span>
        <span class="gift-recipient-name">${esc(g.recipient_name)}</span>
        ${g.in_trust ? `<span class="gift-badge">in trust</span>` : ''}
      </div>
      ${g.offset_against_residuary
        ? `<div class="gift-offset">Value charged against ${esc(g.recipient_name)}'s share of the residue.</div>`
        : ''}
    </li>
  `).join('');

  return [
    slide(
      'Before the residuary — specific gifts',
      `<ul class="gift-list">${items}</ul>`,
      {
        kicker: 'Pre-residuary bequests',
        footnote: 'These gifts are distributed first. Everything that follows applies to the remaining estate.',
      },
    ),
  ];
}

function buildFiduciarySlides(v) {
  const slides = [
    slide(
      'Who carries it out',
      `<p class="lead">The people you've chosen.</p>`,
      { kicker: 'Part 2 — Fiduciaries' },
    ),
    fiduciarySlide('Executor of your Will', [
      { label: 'Primary', name: v.executor_name },
      { label: '1st Successor', name: v.executor_successor_count >= 1 ? v.successor_executor_name : '' },
      { label: '2nd Successor', name: v.executor_successor_count >= 2 ? v.second_successor_executor_name : '' },
    ], { footnote: 'Administers your Will through the probate court.' }),
    fiduciarySlide('Trustee', [
      { label: 'During your lifetime', name: v.client },
      { label: '1st Successor', name: v.successor_trustee_name },
      { label: '2nd Successor', name: v.trustee_successor_count >= 2 ? v.second_successor_trustee_name : '' },
      { label: '3rd Successor', name: v.trustee_successor_count >= 3 ? v.third_successor_trustee_name : '' },
    ], { footnote: 'Manages and distributes Trust assets according to its terms.' }),
    fiduciarySlide('Trust Protector', [
      { label: '1st', name: v.first_successor_trust_protector_name },
      { label: '2nd', name: v.trust_protector_successor_count >= 2 ? v.second_successor_trust_protector_name : '' },
    ], { footnote: 'An independent check on the Trustee; can remove and replace if needed.' }),
    fiduciarySlide('Financial Agent', [
      { label: 'Primary', name: v.agent_name },
      { label: 'Successor', name: v.financial_agent_successor_count >= 1 ? v.successor_agent_name : '' },
    ], {
      kicker: 'Power of Attorney',
      footnote: 'Acts for you on financial matters if you cannot.',
    }),
    fiduciarySlide('Healthcare Agent', [
      { label: 'Primary', name: v.healthcare_agent_name },
      { label: '1st Successor', name: v.healthcare_agent_successor_count >= 1 ? v.first_successor_healthcare_agent_name : '' },
      { label: '2nd Successor', name: v.healthcare_agent_successor_count >= 2 ? v.second_successor_healthcare_agent_name : '' },
    ], { footnote: 'Makes medical decisions for you if you cannot.' }),
  ];

  if (v.has_minor_children) {
    slides.push(fiduciarySlide('Guardian for your minor children', [
      { label: 'Primary', name: v.guardian_name },
      { label: 'Successor', name: v.guardian_successor_count >= 1 ? v.successor_guardian_name : '' },
    ], { footnote: 'Raises your minor children if neither parent can.' }));
  }

  return slides;
}

function buildClosing() {
  return `
    <section class="slide closing">
      <div class="slide-inner">
        <h1 class="headline">That's your plan.</h1>
        <p class="lead">Questions?</p>
        <div class="footnote closing-footnote">
          This is a summary. The signed Will, Trust, and related documents control in all respects.
        </div>
      </div>
    </section>
  `;
}

// ---------------------------------------------------------------------------
// Package-specific disposition slides
// ---------------------------------------------------------------------------

function buildDisclaimerDisposition(v) {
  const slides = [];
  slides.push(slide(
    'At your death',
    `<p class="lead">Your plan begins.</p>`,
    { kicker: 'Part 1 — Disposition' },
  ));

  slides.push(slide(
    `Everything passes to ${esc(v.spouse)}`,
    `<ul class="flow">
       <li>Your <strong>Pour-Over Will</strong> moves all assets into your <strong>Revocable Living Trust</strong>.</li>
       <li>The Trust distributes outright to ${esc(v.spouse)}.</li>
     </ul>`,
  ));

  slides.push(slide(
    `${esc(v.spouse)} has a choice`,
    `<div class="choice">
       <div class="choice-branch">
         <div class="branch-label">Accept</div>
         <div class="branch-detail">Take it outright.</div>
       </div>
       <div class="choice-or">— or —</div>
       <div class="choice-branch">
         <div class="branch-label">Disclaim</div>
         <div class="branch-detail">Disclaimed assets fund the <strong>Family Trust</strong> (credit-shelter) for ${esc(v.spouse)}'s lifetime benefit.</div>
       </div>
     </div>`,
    { kicker: 'Disclaimer election' },
  ));

  slides.push(slide(
    `Upon ${esc(v.spouse)}'s death`,
    `<p class="lead">All remaining assets pass to your descendants.</p>`,
  ));

  slides.push(...buildMinorsSlides(v));
  return slides;
}

function buildQtipDisposition(v) {
  const slides = [];
  slides.push(slide(
    'At your death',
    `<p class="lead">Your plan begins.</p>`,
    { kicker: 'Part 1 — Disposition' },
  ));

  slides.push(slide(
    `The Trust divides into two sub-trusts`,
    `<ul class="flow">
       <li>Your <strong>Pour-Over Will</strong> moves all assets into your <strong>Revocable Living Trust</strong>.</li>
       <li>By formula, the Trust splits into a <strong>Marital Trust</strong> and a <strong>Family Trust</strong>.</li>
     </ul>`,
    { kicker: 'Fractional QTIP split' },
  ));

  slides.push(slide(
    `Two sub-trusts, two purposes`,
    `<div class="choice">
       <div class="choice-branch">
         <div class="branch-label">Marital Trust (QTIP)</div>
         <div class="branch-detail">
           Sheltered by the marital deduction.<br/>
           ${esc(v.spouse)} receives all income for life; principal available for support, maintenance, health.<br/>
           <em>Included in ${esc(v.spouse)}'s estate at death.</em>
         </div>
       </div>
       <div class="choice-or">+</div>
       <div class="choice-branch">
         <div class="branch-label">Family Trust</div>
         <div class="branch-detail">
           Sheltered by your estate tax exemption.<br/>
           Benefits ${esc(v.spouse)} and descendants.<br/>
           <em>Passes outside ${esc(v.spouse)}'s estate.</em>
         </div>
       </div>
     </div>`,
  ));

  slides.push(slide(
    `Upon ${esc(v.spouse)}'s death`,
    `<ul class="flow">
       <li><strong>Marital Trust</strong> assets are included in ${esc(v.spouse)}'s estate for tax, then pass to descendants.</li>
       <li><strong>Family Trust</strong> assets pass to descendants outside ${esc(v.spouse)}'s estate — free of further transfer tax.</li>
     </ul>`,
  ));

  slides.push(...buildMinorsSlides(v));
  return slides;
}

function buildProbateDisposition(v) {
  const slides = [];
  slides.push(slide(
    'At your death',
    `<p class="lead">Your plan begins.</p>`,
    { kicker: 'Part 1 — Disposition' },
  ));

  slides.push(slide(
    `Your Trust avoids probate`,
    `<ul class="flow">
       <li>Your <strong>Pour-Over Will</strong> moves any remaining assets into your <strong>Revocable Living Trust</strong>.</li>
       <li>The Trust distributes directly to your descendants — <strong>without probate court</strong>.</li>
     </ul>`,
  ));

  slides.push(...buildMinorsSlides(v));
  return slides;
}

const DISPOSITION_BUILDERS = {
  [PKG_DISCLAIMER]: buildDisclaimerDisposition,
  [PKG_QTIP]: buildQtipDisposition,
  [PKG_PROBATE]: buildProbateDisposition,
};

// ---------------------------------------------------------------------------
// Slide assembly
// ---------------------------------------------------------------------------

function buildSlides(v) {
  const pkg = detectPackage(v.package_type);
  const slides = [
    buildCover(v),
    buildOverview(),
    ...buildSpecificGiftsSlides(v),
    ...DISPOSITION_BUILDERS[pkg](v),
    buildAgeSlide(v),
    ...buildFiduciarySlides(v),
    buildClosing(),
  ];
  return slides;
}

// ---------------------------------------------------------------------------
// Stylesheet
// ---------------------------------------------------------------------------

const STYLES = `
  :root {
    --ink: #1f2a44;
    --ink-soft: #4a4a4a;
    --paper: #fafaf7;
    --paper-card: #ffffff;
    --accent: #8a6d3b;
    --rule: #e3e0d8;
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { height: 100%; }
  body {
    font-family: 'Georgia', 'Times New Roman', serif;
    background: var(--paper);
    color: var(--ink);
    overflow: hidden;
    -webkit-font-smoothing: antialiased;
  }
  #app {
    position: relative;
    height: 100vh;
    display: flex;
    flex-direction: column;
  }
  #slides {
    flex: 1;
    position: relative;
    overflow: hidden;
  }
  .slide {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3rem 4rem;
    opacity: 0;
    transform: translateY(8px);
    transition: opacity 0.35s ease, transform 0.35s ease;
    pointer-events: none;
  }
  .slide.active {
    opacity: 1;
    transform: none;
    pointer-events: auto;
  }
  .slide-inner {
    max-width: 60rem;
    width: 100%;
    text-align: center;
  }
  .kicker {
    text-transform: uppercase;
    letter-spacing: 0.18em;
    font-size: 0.78rem;
    color: var(--accent);
    margin-bottom: 1.5rem;
    font-family: 'Inter', 'Helvetica Neue', sans-serif;
    font-weight: 600;
  }
  .headline {
    font-size: clamp(2rem, 4.5vw, 3.5rem);
    line-height: 1.15;
    font-weight: 400;
    margin-bottom: 2rem;
    color: var(--ink);
  }
  .cover-title {
    font-size: clamp(2.4rem, 6vw, 4.5rem);
    line-height: 1.1;
    font-weight: 400;
    margin: 1rem 0 2rem;
  }
  .cover-title .and {
    font-style: italic;
    color: var(--ink-soft);
    font-size: 0.75em;
  }
  .cover-meta {
    font-size: 1.1rem;
    color: var(--ink-soft);
    margin-bottom: 4rem;
    font-family: 'Inter', 'Helvetica Neue', sans-serif;
  }
  .cover-cta {
    font-size: 0.9rem;
    color: var(--accent);
    letter-spacing: 0.1em;
    text-transform: uppercase;
    font-family: 'Inter', 'Helvetica Neue', sans-serif;
  }
  .body { font-size: 1.25rem; line-height: 1.6; color: var(--ink-soft); }
  .lead { font-size: 1.4rem; line-height: 1.5; color: var(--ink); }
  .footnote {
    margin-top: 2.5rem;
    font-size: 0.85rem;
    color: var(--ink-soft);
    font-style: italic;
    font-family: 'Inter', 'Helvetica Neue', sans-serif;
  }
  .closing-footnote { font-size: 0.75rem; margin-top: 4rem; }

  /* Flow list */
  .flow {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-width: 42rem;
    margin: 0 auto;
  }
  .flow li {
    background: var(--paper-card);
    border: 1px solid var(--rule);
    border-radius: 6px;
    padding: 1.2rem 1.6rem;
    font-size: 1.15rem;
  }

  /* Big numbered list */
  .big-list {
    list-style: none;
    counter-reset: biglist;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-width: 28rem;
    margin: 0 auto;
  }
  .big-list li {
    counter-increment: biglist;
    font-size: 1.4rem;
    text-align: left;
    padding: 1rem 1.2rem 1rem 3.5rem;
    position: relative;
    background: var(--paper-card);
    border: 1px solid var(--rule);
    border-radius: 6px;
  }
  .big-list li::before {
    content: counter(biglist);
    position: absolute;
    left: 1.2rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--accent);
    font-size: 1.6rem;
    font-family: 'Inter', sans-serif;
    font-weight: 300;
  }

  /* Disclaimer choice / QTIP split */
  .choice {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 1.5rem;
    align-items: center;
    max-width: 52rem;
    margin: 0 auto;
  }
  .choice-branch {
    background: var(--paper-card);
    border: 1px solid var(--rule);
    border-radius: 6px;
    padding: 2rem 1.5rem;
    text-align: center;
  }
  .branch-label {
    font-size: 1.1rem;
    text-transform: uppercase;
    letter-spacing: 0.15em;
    color: var(--accent);
    margin-bottom: 0.8rem;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
  }
  .branch-detail { font-size: 1rem; line-height: 1.5; color: var(--ink-soft); }
  .choice-or {
    color: var(--ink-soft);
    font-style: italic;
    font-size: 1.2rem;
  }

  /* Age callout */
  .age-callout {
    background: var(--paper-card);
    border: 1px solid var(--rule);
    border-radius: 6px;
    padding: 2.5rem 2rem;
    max-width: 40rem;
    margin: 0 auto;
  }
  .age-big {
    font-size: 6rem;
    font-weight: 300;
    color: var(--accent);
    line-height: 1;
    margin-bottom: 1rem;
  }
  .age-caption { font-size: 1.1rem; line-height: 1.6; }

  .age-ladder {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    max-width: 38rem;
    margin: 0 auto;
  }
  .age-rung {
    background: var(--paper-card);
    border: 1px solid var(--rule);
    border-radius: 6px;
    padding: 1rem 1.5rem;
    display: flex;
    align-items: center;
    gap: 1.5rem;
    text-align: left;
  }
  .age-num {
    font-size: 2.2rem;
    color: var(--accent);
    font-weight: 300;
    min-width: 3rem;
    text-align: center;
  }
  .age-text { font-size: 1.1rem; color: var(--ink-soft); }

  /* Specific gifts list */
  .gift-list {
    list-style: none;
    max-width: 46rem;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  .gift-item {
    background: var(--paper-card);
    border: 1px solid var(--rule);
    border-radius: 6px;
    padding: 1.3rem 1.6rem;
    text-align: left;
  }
  .gift-description {
    font-size: 1.05rem;
    line-height: 1.5;
    color: var(--ink);
    margin-bottom: 0.6rem;
  }
  .gift-recipient {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-family: 'Inter', sans-serif;
    flex-wrap: wrap;
  }
  .gift-arrow { color: var(--accent); font-size: 1.2rem; }
  .gift-recipient-name {
    font-size: 1.05rem;
    color: var(--ink);
    font-weight: 500;
  }
  .gift-badge {
    display: inline-block;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--accent);
    border: 1px solid var(--accent);
    border-radius: 3px;
    padding: 0.15rem 0.5rem;
    font-weight: 500;
  }
  .gift-offset {
    margin-top: 0.6rem;
    font-size: 0.85rem;
    color: var(--ink-soft);
    font-style: italic;
    font-family: 'Inter', sans-serif;
  }

  /* Fiduciary list */
  .fid-list {
    list-style: none;
    max-width: 36rem;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }
  .fid-list li {
    background: var(--paper-card);
    border: 1px solid var(--rule);
    border-radius: 6px;
    padding: 1rem 1.5rem;
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 1.5rem;
    text-align: left;
  }
  .fid-list .label {
    color: var(--ink-soft);
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-family: 'Inter', sans-serif;
    font-weight: 500;
  }
  .fid-list .name { font-size: 1.15rem; color: var(--ink); text-align: right; }

  /* Controls */
  .controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.8rem 2rem;
    border-top: 1px solid var(--rule);
    background: var(--paper);
    font-family: 'Inter', sans-serif;
    font-size: 0.85rem;
    color: var(--ink-soft);
  }
  .controls button {
    background: transparent;
    border: 1px solid var(--rule);
    border-radius: 4px;
    padding: 0.5rem 1.2rem;
    color: var(--ink);
    cursor: pointer;
    font-size: 1rem;
    font-family: inherit;
    transition: background 0.15s ease;
  }
  .controls button:hover { background: var(--paper-card); }
  .controls button:disabled { opacity: 0.3; cursor: not-allowed; }
  #counter { font-variant-numeric: tabular-nums; }
  .progress {
    height: 3px;
    background: var(--rule);
    position: relative;
  }
  .progress-bar {
    height: 100%;
    background: var(--accent);
    width: 0;
    transition: width 0.3s ease;
  }
  @media print {
    body { overflow: auto; }
    #app { height: auto; }
    .slide {
      position: static;
      opacity: 1;
      page-break-after: always;
      transform: none;
      min-height: 100vh;
    }
    .controls, .progress { display: none; }
  }
`;

// ---------------------------------------------------------------------------
// JavaScript runtime (injected into output)
// ---------------------------------------------------------------------------

const RUNTIME = `
(function() {
  var slides = document.querySelectorAll('.slide');
  var total = slides.length;
  var current = 0;
  var prevBtn = document.getElementById('prev');
  var nextBtn = document.getElementById('next');
  var counter = document.getElementById('counter');
  var bar = document.querySelector('.progress-bar');

  function show(i) {
    current = Math.max(0, Math.min(total - 1, i));
    for (var j = 0; j < total; j++) {
      slides[j].classList.toggle('active', j === current);
    }
    counter.textContent = (current + 1) + ' / ' + total;
    bar.style.width = (((current + 1) / total) * 100) + '%';
    prevBtn.disabled = current === 0;
    nextBtn.disabled = current === total - 1;
  }

  document.addEventListener('keydown', function(e) {
    if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
      e.preventDefault(); show(current + 1);
    } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
      e.preventDefault(); show(current - 1);
    } else if (e.key === 'Home') {
      e.preventDefault(); show(0);
    } else if (e.key === 'End') {
      e.preventDefault(); show(total - 1);
    }
  });

  prevBtn.addEventListener('click', function() { show(current - 1); });
  nextBtn.addEventListener('click', function() { show(current + 1); });

  document.getElementById('slides').addEventListener('click', function(e) {
    if (e.target.closest('button')) return;
    show(current + 1);
  });

  show(0);
})();
`;

// ---------------------------------------------------------------------------
// Main entry
// ---------------------------------------------------------------------------

/**
 * Generate a self-contained interactive HTML plan.
 *
 * @param {object} renderVars - Flat dict from computeRenderVariables().
 * @returns {string} Complete HTML document as a string.
 */
export function generateInteractivePlanHtml(renderVars) {
  const v = renderVars || {};
  const slidesHtml = buildSlides(v).join('\n');
  const title = v.spouse
    ? `Estate Plan — ${v.client} and ${v.spouse}`
    : `Estate Plan — ${v.client || ''}`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${esc(title)}</title>
<style>${STYLES}</style>
</head>
<body>
<div id="app">
  <main id="slides">
    ${slidesHtml}
  </main>
  <div class="progress"><div class="progress-bar"></div></div>
  <nav class="controls">
    <button id="prev" aria-label="Previous slide">&larr; Back</button>
    <span id="counter">1 / 1</span>
    <button id="next" aria-label="Next slide">Next &rarr;</button>
  </nav>
</div>
<script>${RUNTIME}</script>
</body>
</html>`;
}

// Convenience: trigger a download of the plan HTML.
export function downloadInteractivePlan(renderVars, filename = 'estate-plan.html') {
  const html = generateInteractivePlanHtml(renderVars);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Convenience: open the plan in a new tab.
export function openInteractivePlan(renderVars) {
  const html = generateInteractivePlanHtml(renderVars);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return w;
}
